import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { CryptoService } from './crypto.service';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private readonly localStorage = this.resolveStorage();
  private readonly cryptoService = inject(CryptoService);

  get<T>(key: string): T | null {
    const item = this.localStorage?.getItem(key);

    if (!item) {
      return null;
    }

    return this.isJSONValid(item) ? (JSON.parse(item) as T) : (item as T);
  }

  set(key: string, value: unknown): void {
    this.localStorage?.setItem(key, JSON.stringify(value));
  }

  getEncrypt<T>(key: string): T | null {
    if (!environment.encrypt) {
      return this.get<T>(key);
    }

    const hashString = this.localStorage?.getItem(key);

    if (
      !hashString ||
      (hashString && this.cryptoService.decrypt(hashString) === null)
    ) {
      return null;
    }

    const value = this.cryptoService.decrypt<string>(hashString)!;
    return this.isJSONValid(value) ? (JSON.parse(value) as T) : (value as T);
  }

  setEncrypt(key: string, value: unknown) {
    if (!environment.encrypt) {
      this.set(key, value);
      return;
    }

    this.localStorage?.setItem(
      key,
      this.cryptoService.encrypt(JSON.stringify(value)),
    );
  }

  remove(key: string): void {
    this.localStorage?.removeItem(key);
  }

  removeKeys(keys: string[]): void {
    keys.forEach((key) => this.localStorage?.removeItem(key));
  }

  clear(): void {
    this.localStorage?.clear();
  }

  // Form nhúng iframe ở web đại lý: trình duyệt ẩn danh / chặn cookie bên thứ ba
  // làm `window.localStorage` ném SecurityError. Lỗi nổ ngay lúc khởi tạo service
  // nên cả app không bootstrap được (trang trắng) → dùng bộ nhớ tạm khi bị chặn.
  private resolveStorage(): Pick<
    Storage,
    'getItem' | 'setItem' | 'removeItem' | 'clear'
  > {
    try {
      const storage = inject(DOCUMENT)?.defaultView?.localStorage;
      if (storage) {
        return storage;
      }
    } catch (error) {
      // bị chặn → rơi xuống bộ nhớ tạm
    }

    const memory = new Map<string, string>();
    return {
      getItem: (key) => memory.get(key) ?? null,
      setItem: (key, value) => void memory.set(key, String(value)),
      removeItem: (key) => void memory.delete(key),
      clear: () => memory.clear(),
    };
  }

  private isJSONValid(value: string): boolean {
    try {
      JSON.parse(value);
      return true;
    } catch (error) {
      return false;
    }
  }
}
